// Dynamics 365 Customer Insights - Journeys has no documented plain-fetch submit
// endpoint. The supported integration is Microsoft's FormLoader bundle, which fetches
// the real form and owns the internal submit URL. We render that form off-screen,
// prefill the email we already collected, and submit it programmatically so the
// registration UX stays a single checkbox.
const EN_FORM_ID = "ad013a8b-c273-f111-ab0e-000d3adc3993";
const DE_FORM_ID = "4c54dd52-c173-f111-ab0e-000d3adc3993";
const ORG_ID = "f763b8fc-e248-4fa8-9dd9-021221025580";
const FORM_API_BASE_URL = `https://public-eur.mkt.dynamics.com/api/v1.0/orgs/${ORG_ID}/landingpageforms`;
const DIGITAL_ASSETS_BASE = `https://assets-eur.mkt.dynamics.com/${ORG_ID}/digitalassets/forms`;
const FORM_LOADER_SRC = "https://formui-usa1.mkt.dynamics.com/eur/FormLoader/FormLoader.bundle.js";

const GIVE_UP_MS = 15_000;

type FormLoader = {
  createForm: (
    formId: string,
    formApiBaseUrl: string,
    formUrl: string,
    attributes?: Record<string, string>
  ) => HTMLDivElement;
};

declare global {
  interface Window {
    d365mktforms?: FormLoader;
  }
}

export const submitNewsletterSignup = async (email: string): Promise<void> => {
  try {
    const formLoader = await ensureFormLoaderReady();
    const hiddenForm = renderHiddenForm(formLoader, pickFormId());
    await fillAndSubmitWhenReady(hiddenForm, email);
  } catch {
    // Swallow errors — newsletter failure must not surface to the user.
  }
};

const ensureFormLoaderReady = async (): Promise<FormLoader> => {
  await loadFormLoaderScript();
  if (!window.d365mktforms) throw new Error("FormLoader did not initialise");
  return window.d365mktforms;
};

let loaderPromise: Promise<void> | undefined;

const loadFormLoaderScript = (): Promise<void> => {
  if (window.d365mktforms) return Promise.resolve();
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = FORM_LOADER_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loaderPromise = undefined; // allow a later retry
      reject(new Error("FormLoader failed to load"));
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
};

const pickFormId = (): string => (navigator.language.startsWith("de") ? DE_FORM_ID : EN_FORM_ID);

const renderHiddenForm = (formLoader: FormLoader, formId: string): HTMLDivElement => {
  const formUrl = `${DIGITAL_ASSETS_BASE}/${formId}`;
  const placeholder = formLoader.createForm(formId, FORM_API_BASE_URL, formUrl, {
    "data-preventsubmissionui": "true", // suppress Microsoft's success/error banner
  });

  placeholder.style.position = "absolute";
  placeholder.style.left = "-9999px";
  placeholder.setAttribute("aria-hidden", "true");
  document.body.appendChild(placeholder);

  return placeholder;
};

// The bundle fetches the form async, so we wait for its load event, then submit
// and resolve on its submit event. A timeout drops the node if neither fires.
const fillAndSubmitWhenReady = (placeholder: HTMLDivElement, email: string): Promise<void> =>
  new Promise((resolve) => {
    let giveUpTimer: number | undefined;

    const finish = () => {
      if (giveUpTimer !== undefined) window.clearTimeout(giveUpTimer);
      document.removeEventListener("d365mkt-afterformload", onAfterLoad);
      document.removeEventListener("d365mkt-afterformsubmit", onAfterSubmit);
      placeholder.remove();
      resolve();
    };

    const onAfterLoad = (event: Event) => {
      if (!placeholder.contains(event.target as Node)) return;
      document.removeEventListener("d365mkt-afterformload", onAfterLoad);
      if (!fillAndSubmit(placeholder, email)) finish();
    };

    const onAfterSubmit = (event: Event) => {
      if (placeholder.contains(event.target as Node)) finish();
    };

    document.addEventListener("d365mkt-afterformload", onAfterLoad);
    document.addEventListener("d365mkt-afterformsubmit", onAfterSubmit);
    giveUpTimer = window.setTimeout(finish, GIVE_UP_MS);
  });

// Returns false when the loaded form has no email field (nothing to submit).
const fillAndSubmit = (placeholder: HTMLDivElement, email: string): boolean => {
  const form = placeholder.querySelector("form");
  const emailInput = form?.querySelector<HTMLInputElement>('[name="emailaddress1"]');
  if (!form || !emailInput) return false;

  setControlledInputValue(emailInput, email);

  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (submitButton) submitButton.click();
  else form.requestSubmit();

  return true;
};

// Microsoft's form is React-controlled, so a plain `input.value =` is ignored.
// Use the native value setter then dispatch input/change to update its internal state.
const setControlledInputValue = (input: HTMLInputElement, value: string): void => {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  setter?.call(input, value);
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
};
